import React from 'react';
import { 
  DocumentTextIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { OmbudsmanStatistics } from '../../types/ombudsman';
import { Timestamp } from '../ui/timestamp';

interface OmbudsmanStatsDashboardProps {
  statistics: OmbudsmanStatistics;
}

const OmbudsmanStatsDashboard: React.FC<OmbudsmanStatsDashboardProps> = ({ statistics }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Upheld':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      case 'Not Upheld':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'Partially Upheld':
        return <ClockIcon className="h-6 w-6 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Upheld':
        return 'bg-red-50 border-red-200';
      case 'Not Upheld':
        return 'bg-green-50 border-green-200';
      case 'Partially Upheld':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getImportStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'partial':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getImportStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-800 bg-green-100';
      case 'failed':
        return 'text-red-800 bg-red-100';
      case 'partial':
        return 'text-yellow-800 bg-yellow-100';
      default:
        return 'text-gray-800 bg-gray-100';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Overview Cards */}
      <div className="lg:col-span-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total Decisions */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Decisions</p>
                <p className="text-2xl font-semibold text-gray-900">{statistics.total_decisions.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Vector Readiness */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 md:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Vector Readiness (full text)</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {(() => {
                    const vs = statistics.vector_stats;
                    const ready = vs?.full_text_vectors ?? 0;
                    return `${ready.toLocaleString()} / ${statistics.total_decisions.toLocaleString()}`;
                  })()}
                </p>
              </div>
              <div className="text-right text-sm text-gray-600">
                {statistics.vector_stats?.pct_full_text_vectors != null && (
                  <span>{Math.round((statistics.vector_stats.pct_full_text_vectors || 0) * 100)}%</span>
                )}
              </div>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2" aria-hidden>
              <div
                className="h-2 rounded-full bg-blue-600"
                style={{ width: `${Math.min(100, Math.round(((statistics.vector_stats?.pct_full_text_vectors || 0) * 100)))}%` }}
              />
            </div>
            {statistics.vector_stats && statistics.vector_stats.full_text_json && statistics.vector_stats.full_text_vectors < statistics.total_decisions && (
              <div className="mt-2 text-xs text-gray-600">
                JSON embeddings present: {statistics.vector_stats.full_text_json.toLocaleString()} • waiting on vector backfill for similarity search
              </div>
            )}
          </div>

          {/* Upheld Decisions */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Upheld</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {(statistics.decisions_by_status['Upheld'] || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  {statistics.total_decisions > 0 
                    ? `${((statistics.decisions_by_status['Upheld'] || 0) / statistics.total_decisions * 100).toFixed(1)}%`
                    : '0%'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Not Upheld Decisions */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Not Upheld</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {(statistics.decisions_by_status['Not Upheld'] || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  {statistics.total_decisions > 0 
                    ? `${((statistics.decisions_by_status['Not Upheld'] || 0) / statistics.total_decisions * 100).toFixed(1)}%`
                    : '0%'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Partially Upheld Decisions */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Partially Upheld</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {(statistics.decisions_by_status['Partially Upheld'] || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  {statistics.total_decisions > 0 
                    ? `${((statistics.decisions_by_status['Partially Upheld'] || 0) / statistics.total_decisions * 100).toFixed(1)}%`
                    : '0%'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decision Status Breakdown */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <ChartBarIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Decision Status Breakdown</h3>
        </div>
        <div className="space-y-3">
          {Object.entries(statistics.decisions_by_status).map(([status, count]) => (
            <div key={status} className={`p-3 rounded-lg border ${getStatusColor(status)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(status)}
                  <span className="font-medium text-gray-900">{status}</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">{count.toLocaleString()}</div>
                  <div className="text-xs text-gray-600">
                    {statistics.total_decisions > 0 
                      ? `${(count / statistics.total_decisions * 100).toFixed(1)}%`
                      : '0%'
                    }
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Industry Sector Breakdown */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <ChartBarIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Industry Sectors</h3>
        </div>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {Object.entries(statistics.decisions_by_sector)
            .sort(([,a], [,b]) => b - a)
            .map(([sector, count]) => (
            <div key={sector} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="font-medium text-gray-900">{sector}</span>
              <div className="text-right">
                <div className="font-semibold text-gray-900">{count.toLocaleString()}</div>
                <div className="text-xs text-gray-600">
                  {statistics.total_decisions > 0 
                    ? `${(count / statistics.total_decisions * 100).toFixed(1)}%`
                    : '0%'
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Imports */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Recent Imports</h3>
        </div>
        {statistics.recent_imports.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No imports yet
          </div>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {statistics.recent_imports.map((batch) => (
              <div key={batch.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getImportStatusIcon(batch.status)}
                    <span className="font-medium text-gray-900 truncate max-w-32">
                      {batch.filename}
                    </span>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getImportStatusColor(batch.status)}`}>
                    {batch.status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                  <div>Total: {batch.total_decisions}</div>
                  <div>Success: {batch.imported_decisions}</div>
                  <div>Failed: {batch.failed_decisions}</div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  <Timestamp value={batch.created_at} format="date-only" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OmbudsmanStatsDashboard;
